import { ref, Ref } from 'vue'
import type { DeepPartial } from 'utility-types'
import { SECOND, MINUTE } from '@shared/constants/time'

/** 小工具操作指令 */
export const PluginAction = Object.freeze({
  STOP: 'stop',
  RUN: 'run',
  INSTALL: 'install',
  UNINSTALL: 'uninstall',
  CLEAN: 'clean',
  UPDATE: 'update',
})

/** 小工具操作结果 */
const PluginActionStatus = Object.freeze({
  PHASE_TYPE_PENDING: 'PHASE_TYPE_PENDING',
  PHASE_TYPE_RUNNING: 'PHASE_TYPE_RUNNING',
  PHASE_TYPE_ERROR: 'PHASE_TYPE_ERROR',
  PHASE_TYPE_COMPLETE: 'PHASE_TYPE_COMPLETE',
})

/** 小工具状态 */
const PluginStatus = Object.freeze({
  RUNNING: 'running',
  STOPPED: 'stopped',
})

/**
 * 小工具按钮文案
 */
export const pluginButton = Object.freeze({
  OPEN: '打开',
  UNINSTALL: '卸载',
  STOP: '停止',
  RUN: '启动',
  INSTALL: '安装',
  UPDATE: '更新',
  CLEAN: '移除',
  IN_STOP: '停止中',
  IN_UPDATE: '更新中',
  IN_UNINSTALL: '卸载中',
  IN_CLEAN: '移除中',
  IN_RUN: '启动中',
  IN_INSTALL: '安装中',
})

export const inProgressText = Object.freeze({
  NOT_IN_PROGRESS: 'not-in-progress',
  IN_PROGRESS: 'in-progress',
  IN_PERCENT: 'in-percent',
})

export interface IPluginEle {
  /**
   *  - PHASE_TYPE_PENDING: 等待中
   *  - PHASE_TYPE_RUNNING: 进行中
   *  - PHASE_TYPE_ERROR: 失败
   *  - PHASE_TYPE_COMPLETE: 完成
   */
  statusPhase: string
  /**
   * 失败信息
   */
  message: string
  /**
   * 操作指令
   * 'stop'|'run'|'install'|'uninstall'|'clean'|'update'
   */
  action: string
  /**
   * 操作id
   */
  statusId: string
  /**
   * 进度 % 百分比
   */
  progress: number
  /**
   * 小工具状态 
   * - running:运行中
   * - stopped:暂停
   */
  pluginStatus: 'running' | 'stopped'
  /** 
   * 应用ID，每个应用拥有一个唯一ID 
   */
  id: string
  /**
   * 应用图标
   */
  iconLink: string
  /**
   * 应用链接
   */
  link: string
  /**
   * 应用名称
   */
  name: string
  /**
   * 说明
   */
  description: string
  /**
   * 作者
   */
  author: string
  /**
   * 是否展示下拉框
   */
  showAction: false
  /**
   * 下拉框列表
   */
  moreAction: string[]
  /**
   * 按钮中间态
   * - not-in-progress 不是中间态
   * - in-progress 显示进行中
   * - in-percent 展示百分比
   */
  inProgress: string
  /**
   * 按钮文字描述
   */
  statusPhaseDesc: string
  version?: string
}

/**
 * 获取小工具列表
 * 调用小工具轮询接口
 * 以及关闭轮询
 */
export function usePluginMall ({
  space,
  getPluginApp,
  getPluginStatus,
  deleteStatus,
  showError,
}: {
  space: Ref<string>
  getPluginApp: (space: string) => Promise<DeepPartial<API_PLUGIN_MALL.GetPluginApp>>
  getPluginStatus: (space: string) => Promise<DeepPartial<API_PLUGIN_MALL.GetPluginStatus>>
  deleteStatus: (space: string, taskIds: string) => Promise<void>
  showError: (errorText: string) => void
}) {

  /**
   * 轮询定时器
   */
  const pollTimeoutRef = ref<number | null>(null)

  /**
   * 整合小工具信息及状态变更结果的列表
   */
  const pluginData = ref<Partial<IPluginEle>[]>([])

  /**
   * 获取小工具应用配置信息列表
   */
  const pluginApp = ref<DeepPartial<API_PLUGIN_MALL.PluginApps>[]>([])

  /**
   * 去掉定时器
   */
  function clearAppTimer () {
    if (pollTimeoutRef.value) clearTimeout(pollTimeoutRef.value)
  }

  let isInQuickPoll = false

  /**
   * 轮询小工具
   */
  const pollPlugin = async (quickPollTimes = 0) => {
    clearAppTimer()
    const pluginInfo = await getPluginApp(space.value).catch(e => {console.error(e); return {} as API_PLUGIN_MALL.GetPluginApp})
    const pluginStatus = await getPluginStatus(space.value).catch(e => {console.error(e); return {} as API_PLUGIN_MALL.GetPluginStatus})
    pluginApp.value = pluginInfo?.apps ?? []
    /**
     * 获取对小工具进行状态变更操作结果的列表
     */
    const pluginState = pluginStatus?.tasks ?? []
    
    /**
     * 整合小工具信息及状态变更结果的临时列表
     */
    const pluginTempData: Partial<IPluginEle>[] = []

    pluginApp.value.forEach((pluginAppItem) => {
      const index = pluginState.findIndex((item) => item.params?.app_id === pluginAppItem.id)

      const pluginEle:  Partial<IPluginEle> = {
        statusPhase: index !== -1 ? pluginState[index].phase : '',
        message: index !== -1 ? pluginState[index]?.params?.error : '',
        action: index !== -1 ? pluginState[index]?.params?.action : '',
        statusId: index !== -1 ? pluginState[index].id : '',
        progress: index !== -1 ? pluginState[index].progress || 0 : 0,
        pluginStatus: pluginAppItem?.params?.status,
        id: pluginAppItem?.id,
        iconLink: pluginAppItem?.icon_link,
        link: pluginAppItem?.link,
        name: pluginAppItem?.name,
        description: pluginAppItem?.params?.description,
        author: pluginAppItem?.params?.author,
        showAction: false,
        moreAction: [],
        inProgress: inProgressText.NOT_IN_PROGRESS,
        statusPhaseDesc: '',
        version: pluginAppItem?.params?.version,
      }

      const delParams = pluginState[index]?.id ?? ''

      const { pluginStatus = '', action = '', statusPhase = '' } = pluginEle

      /**
       * 改变小工具状态
       * @param statusPhaseDesc 
       * @param inProgress 
       */
      const changePluginEleStatus = (statusPhaseDesc, inProgress) => {
        const lastStatusPhase = pluginEle.statusPhaseDesc
        pluginEle.statusPhaseDesc = statusPhaseDesc
        pluginEle.inProgress = inProgress
        if (lastStatusPhase !== statusPhaseDesc && !isInQuickPoll) {
          pollPlugin(3)
        }
      }
      /**
       * 清空下拉列表
       */
      const clearMoreAction = () => {
        pluginEle.moreAction = []
      }

      /**
       * 小工具运行中对action的判断
       */
      const pluginRunningSwitch = () => {
        pluginEle.statusPhaseDesc = pluginButton.OPEN
        pluginEle.moreAction = [pluginButton.UNINSTALL, pluginButton.STOP]
        switch (action) {
          case PluginAction.STOP:
            if ([PluginActionStatus.PHASE_TYPE_PENDING, PluginActionStatus.PHASE_TYPE_RUNNING].includes(statusPhase)) {
              changePluginEleStatus(pluginButton.IN_STOP, inProgressText.IN_PROGRESS)
              clearMoreAction()
            } else {
              if (statusPhase === PluginActionStatus.PHASE_TYPE_ERROR) {
                showError('暂停失败')
              }
              deleteStatus(space.value, delParams)
            }
            break
          case PluginAction.UPDATE:
            if ([PluginActionStatus.PHASE_TYPE_PENDING, PluginActionStatus.PHASE_TYPE_RUNNING].includes(statusPhase)) {
              changePluginEleStatus(pluginButton.IN_UPDATE, inProgressText.IN_PERCENT)
              clearMoreAction()
            } else {
              if (statusPhase === PluginActionStatus.PHASE_TYPE_ERROR) {
                showError('更新失败')
              }
              deleteStatus(space.value, delParams)
            }
            break
          case PluginAction.UNINSTALL:
            if ([PluginActionStatus.PHASE_TYPE_PENDING, PluginActionStatus.PHASE_TYPE_RUNNING].includes(statusPhase)) {
              changePluginEleStatus(pluginButton.IN_UNINSTALL, inProgressText.IN_PROGRESS)
              clearMoreAction()
            } else {
              if (statusPhase === PluginActionStatus.PHASE_TYPE_ERROR) {
                showError('卸载失败')
              }
              deleteStatus(space.value, delParams)
            }
            break
          case PluginAction.CLEAN:
            if ([PluginActionStatus.PHASE_TYPE_PENDING, PluginActionStatus.PHASE_TYPE_RUNNING].includes(statusPhase)) {
              changePluginEleStatus(pluginButton.IN_CLEAN, inProgressText.IN_PROGRESS)
              clearMoreAction()
            } else {
              if (statusPhase === PluginActionStatus.PHASE_TYPE_ERROR) {
                showError('移除失败')
              }
              deleteStatus(space.value, delParams)
            }
            break
        }
      }

      /**
       * 小工具暂停时对action的判断
       */
      const pluginStoppedSwitch = () => {
        pluginEle.statusPhaseDesc = pluginButton.RUN
        pluginEle.moreAction = [pluginButton.UNINSTALL]
        switch (action) {
          case PluginAction.RUN:
            if ([PluginActionStatus.PHASE_TYPE_PENDING, PluginActionStatus.PHASE_TYPE_RUNNING].includes(statusPhase)) {
              changePluginEleStatus(pluginButton.IN_RUN, inProgressText.IN_PROGRESS)
              clearMoreAction()
            } else {
              if (statusPhase === PluginActionStatus.PHASE_TYPE_ERROR) {
                showError(`启动失败.${pluginEle.message}`)
              }
              deleteStatus(space.value, delParams)
            }
            break
          case PluginAction.UPDATE:
            if ([PluginActionStatus.PHASE_TYPE_PENDING, PluginActionStatus.PHASE_TYPE_RUNNING].includes(statusPhase)) {
              changePluginEleStatus(pluginButton.IN_UPDATE, inProgressText.IN_PERCENT)
              clearMoreAction()
            } else {
              if (statusPhase === PluginActionStatus.PHASE_TYPE_ERROR) {
                showError('更新失败')
              }
              deleteStatus(space.value, delParams)
            }
            break
          case PluginAction.UNINSTALL:
            if ([PluginActionStatus.PHASE_TYPE_PENDING, PluginActionStatus.PHASE_TYPE_RUNNING].includes(statusPhase)) {
              changePluginEleStatus(pluginButton.IN_UNINSTALL, inProgressText.IN_PROGRESS)
              clearMoreAction()
            } else {
              if (statusPhase === PluginActionStatus.PHASE_TYPE_ERROR) {
                showError('卸载失败')
              }
              deleteStatus(space.value, delParams)
            }
            break
          case PluginAction.CLEAN:
            if ([PluginActionStatus.PHASE_TYPE_PENDING, PluginActionStatus.PHASE_TYPE_RUNNING].includes(statusPhase)) {
              changePluginEleStatus(pluginButton.IN_CLEAN, inProgressText.IN_PROGRESS)
              clearMoreAction()
            } else {
              if (statusPhase === PluginActionStatus.PHASE_TYPE_ERROR) {
                showError('移除失败')
              }
              deleteStatus(space.value, delParams)
            }
            break
        }
      }

      /**
       * 小工具不存在时对action的判断
       */
      const noPluginSwitch = () => {
        pluginEle.statusPhaseDesc = pluginButton.INSTALL
        if (action === PluginAction.INSTALL) {
          if ([PluginActionStatus.PHASE_TYPE_PENDING, PluginActionStatus.PHASE_TYPE_RUNNING].includes(statusPhase)) {
            changePluginEleStatus(pluginButton.IN_INSTALL, inProgressText.IN_PERCENT)
          } else {
            if (PluginActionStatus.PHASE_TYPE_ERROR === statusPhase) {
              showError('安装失败')
            }
            deleteStatus(space.value, delParams)
          }
        }
      }

      if (pluginStatus === PluginStatus.RUNNING) {
        pluginRunningSwitch()
      } else if (pluginStatus === PluginStatus.STOPPED) {
        pluginStoppedSwitch()
      } else {
        noPluginSwitch()
      }

      if (pluginAppItem?.params?.is_need_update && pluginEle.moreAction && pluginEle.statusPhaseDesc) {
        pluginEle.moreAction.unshift(pluginEle.statusPhaseDesc)
        pluginEle.statusPhaseDesc = pluginButton.UPDATE
      }

      pluginTempData.push(pluginEle)
    })

    // 轮询
    let pollInterval = SECOND * (pluginStatus.expires_in ?? 1)
    if (quickPollTimes > 0) {
      isInQuickPoll = true
      pollInterval = 5 * SECOND
    } else {
      isInQuickPoll = false
    }
    
    console.log('pollInterval', pollInterval)
    pollTimeoutRef.value = window.setTimeout(
      () => pollPlugin(quickPollTimes - 1),
      pollInterval,
    )
    
    pluginData.value = pluginTempData
  }

  return {
    clearAppTimer,
    pollPlugin,
    pluginData,
  }
}